import { API_BASE } from '../config';

export type Track = {
  id: string;
  title: string;
  artist: string;
  filename: string;
  cover_filename?: string | null;
  album?: string | null;
};

export type TrackDetail = Track & {
  lyrics?: string | null;
  lyrics_lrc?: string | null;
  duration_sec?: number | null;
};

type SearchResult = {
  type: string;
  id: string;
  title: string;
  artist?: string;
  album?: string | null;
  cover_filename?: string | null;
  filename?: string;
};

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

function trackFromSearch(r: SearchResult): Track | null {
  if (r.type !== 'song' || !r.id) return null;
  const filename = r.filename?.trim() || `${r.id}.mp3`;
  return {
    id: r.id,
    title: r.title || 'Untitled',
    artist: r.artist || 'Unknown',
    filename,
    cover_filename: r.cover_filename ?? null,
    album: r.album ?? null,
  };
}

export function trackAudioUrl(track: Pick<Track, 'filename'>): string {
  return `${API_BASE}/music/${encodeURIComponent(track.filename)}?v=1`;
}

export function trackCoverUrl(cover?: string | null): string | null {
  if (!cover) return null;
  return `${API_BASE}/art/${encodeURIComponent(cover)}`;
}

export async function fetchNewest(limit = 24): Promise<Track[]> {
  const data = await getJson<{ results: SearchResult[] }>(
    `/api/search?category=new&bucket=7d&limit=${limit}`,
  );
  return data.results.map(trackFromSearch).filter((t): t is Track => t !== null);
}

export async function searchSongs(q: string, limit = 30): Promise<Track[]> {
  const data = await getJson<{ results: SearchResult[] }>(
    `/api/search?q=${encodeURIComponent(q)}&category=song&limit=${limit}`,
  );
  return data.results.map(trackFromSearch).filter((t): t is Track => t !== null);
}

/** Full track payload including lyrics / lyrics_lrc. */
export async function fetchTrackDetail(id: string): Promise<TrackDetail> {
  const data = await getJson<{ track: TrackDetail } | TrackDetail>(`/api/track/${encodeURIComponent(id)}`);
  if (data && typeof data === 'object' && 'track' in data && data.track) {
    return data.track;
  }
  return data as TrackDetail;
}
